import { useRef, useState } from "react"
import type { JSX } from "react"

// Sin extensión `.tsx` a propósito: los exámenes `ca-16-fecha-arriba` y
// `ca-33-identificador-burbuja` (`gates/r1/R4-UISC1-filas.gate.ts:187,209`)
// exigen que el literal del import TERMINE en `celda-fecha-hora` /
// `identificador-acortado` pegado a la comilla, así que la ruta va sin
// extensión. La resolución la cubre `moduleResolution: bundler`.
import { CeldaFechaHora } from "../celda-fecha-hora"
import { IdentificadorAcortado } from "../identificador-acortado"
import { formatearFecha } from "../formato-fecha.ts"
import { segmentoParaAbrir, textoOGuion } from "./cable.ts"
import { recortarEnPalabra } from "./recorte.ts"
import type { Atencion, Caso } from "./tipos.ts"

/**
 * Una fila de la tabla de «Situaciones críticas» (CA-16, CA-33, CA-34, CA-36,
 * CA-45/46, CA-47/48).
 *
 * Por qué la fila maneja su propio «en vuelo» (CA-75): el botón «Marcar como
 * atendido» tiene que estar deshabilitado desde que se toca hasta que responde
 * el servidor. Ese estado es local a la fila: marcar un caso no bloquea los
 * demás.
 *
 * Por qué la respuesta se recorta acá y no en el panel: la tabla muestra la
 * respuesta a 100 caracteres en una línea (regla 9); el panel la muestra
 * COMPLETA. Son dos vistas distintas y el recorte de la tabla vive en
 * `recorte.ts`.
 */
export function FilaDeCaso(props: {
  readonly caso: Caso
  readonly onVerConversacion: (idConversacion: string) => void
  readonly onMarcar: (id: string) => Promise<Atencion>
}): JSX.Element {
  const { caso } = props
  const [atencion, setAtencion] = useState<Atencion>(caso.atencion)
  const [marcando, setMarcando] = useState(false)
  const [errorDeMarca, setErrorDeMarca] = useState<string | null>(null)
  // Corte SÍNCRONO contra la doble marca. `marcando` (estado) no sirve para
  // esto: React agrupa los `setState`, así que un segundo clic dentro del mismo
  // tick todavía lo lee en `false`. El ref cambia en el acto.
  const enVuelo = useRef(false)

  async function alMarcar(): Promise<void> {
    // Un segundo clic dentro del mismo tick no debe mandar dos marcas: React
    // recién deshabilita el botón DESPUÉS del re-render. El corte síncrono con
    // `enVuelo.current` es lo que hace cierto el «desde que se toca hasta que
    // responde» (CA-75); `disabled={marcando}` cubre los clics posteriores al
    // render, no los del mismo tick.
    if (enVuelo.current) return
    enVuelo.current = true
    setMarcando(true)
    setErrorDeMarca(null)
    try {
      // El estado de atención se pinta con lo que DEVUELVE el servidor: si la
      // marca falla, el caso sigue «Sin atender» (CA-72/D8).
      setAtencion(await props.onMarcar(caso.id))
    } catch {
      setErrorDeMarca("No se pudo marcar el caso. Intenta de nuevo.")
    } finally {
      enVuelo.current = false
      setMarcando(false)
    }
  }

  const atendido = atencion.estado === "atendido"
  const aAbrir = segmentoParaAbrir(caso)

  return (
    <tr className="situaciones-criticas__fila">
      <td className="situaciones-criticas__celda-fecha">
        <CeldaFechaHora fecha={caso.fecha} />
      </td>
      <td className="situaciones-criticas__celda-conversacion">
        {caso.id_conversacion === null ? (
          "—"
        ) : (
          <IdentificadorAcortado identificador={caso.id_conversacion} que="Conversación" />
        )}
      </td>
      <td className="situaciones-criticas__celda-categoria">{caso.categoria}</td>
      <td className="situaciones-criticas__celda-participante">
        {caso.participante === null ? (
          // Caso anterior a R4: sin participante ⇒ UNA sola «—» (CP-R4-F1-047), no una por dato.
          "—"
        ) : (
          <>
            <span className="situaciones-criticas__participante-nombre">{caso.participante.nombre}</span>
            <span className="situaciones-criticas__participante-telefono">
              {textoOGuion(caso.participante.telefono)}
            </span>
            <span className="situaciones-criticas__participante-lote">
              {textoOGuion(caso.participante.lote)}
            </span>
          </>
        )}
      </td>
      <td className="situaciones-criticas__celda-escribio">
        {/* El mensaje del participante va entre comillas angulares (PRD:883). */}
        {caso.escribio === null ? "No se guardó el mensaje de este caso." : `«${caso.escribio}»`}
      </td>
      <td className="situaciones-criticas__celda-respondio">
        {caso.respondio === null ? "—" : recortarEnPalabra(caso.respondio)}
        {/*
          Las líneas de ayuda van DEBAJO de la respuesta recortada y UNA por
          renglón (CA-39/CA-43). El servidor manda `lineasDeAyuda` aparte de
          `respondio` (Decisión 12): la vista las pinta tal cual, sin volver a
          sumar el sufijo «Línea de ayuda:».
        */}
        {caso.lineasDeAyuda.map((linea) => (
          <p key={linea} className="situaciones-criticas__linea-de-ayuda">
            {linea}
          </p>
        ))}
      </td>
      <td className="situaciones-criticas__celda-estado">
        {atendido ? (
          <>
            {/* CA-44/CA-67 · tres renglones: «Atendido», quién lo marcó y cuándo
                (`dd-mm-aaaa hh:mm`, hora de Chile, en UN renglón). */}
            <span className="situaciones-criticas__estado-linea">Atendido</span>
            <span className="situaciones-criticas__estado-linea">{atencion.por ?? "—"}</span>
            {atencion.cuando !== null && (
              <span className="situaciones-criticas__estado-linea">
                {atencion.cuandoTexto ?? formatearFecha(atencion.cuando)}
              </span>
            )}
          </>
        ) : (
          <span>Sin atender</span>
        )}
      </td>
      <td className="situaciones-criticas__celda-acciones">
        {aAbrir !== null && (
          <button type="button" onClick={() => props.onVerConversacion(aAbrir)}>
            Ver conversación
          </button>
        )}
        {!atendido && (
          <button type="button" disabled={marcando} onClick={alMarcar}>
            Marcar como atendido
          </button>
        )}
        {errorDeMarca !== null && (
          <p className="situaciones-criticas__error-de-marca">{errorDeMarca}</p>
        )}
      </td>
    </tr>
  )
}
